import "dayjs/locale/es";
import "dayjs/locale/fr";

import { enGB, enUS, es, fr } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import i18nInstance from "../../i18n";

// Locale objects for the three languages the app ships (see src/i18n.ts).
// Anything else falls back to English, matching the i18next fallback rules.
const DATE_FNS_LOCALES: Record<string, Locale> = {
	en: enUS,
	fr,
	es,
};

// dayjs takes a locale *name*, not an object, and the name only resolves once
// its locale data has been imported (see the side-effect imports above).
const DAYJS_LOCALES: Record<string, string> = {
	en: "en",
	fr: "fr",
	es: "es",
};

// i18next hands back tags like "fr-CA"; the date libraries only know the base
// language here, so strip the region before looking it up.
const baseLanguage = (language?: string): string =>
	(language ?? "en").split("-")[0].toLowerCase();

export const getDateFnsLocale = (language?: string): Locale =>
	DATE_FNS_LOCALES[baseLanguage(language)] ?? enUS;

export const getDayjsLocale = (language?: string): string =>
	DAYJS_LOCALES[baseLanguage(language)] ?? "en";

/**
 * date-fns locale for the current UI language, for helpers that format dates
 * outside a component. Inside a component prefer `useDateFnsLocale`, which
 * re-renders the component when the language changes.
 */
export const getCurrentDateFnsLocale = (): Locale =>
	getDateFnsLocale(i18nInstance.language);

/**
 * date-fns locale for the language the supervisor picked. Pass it to `format`
 * (`format(date, "MMM d", { locale })`) or to a MUI `LocalizationProvider`
 * (`adapterLocale={locale}`) so month and day names follow the UI language.
 */
export const useDateFnsLocale = (): Locale => {
	const { i18n } = useTranslation();
	return useMemo(() => getDateFnsLocale(i18n.language), [i18n.language]);
};

/**
 * date-fns locale for the calendar pickers. English resolves to en-GB rather
 * than en-US so the calendars keep the Monday-first week they have always
 * rendered; fr and es are Monday-first too, so only the month and day names
 * change when the language does.
 */
export const useDatePickerLocale = (): Locale => {
	const { i18n } = useTranslation();
	return useMemo(() => {
		const locale = getDateFnsLocale(i18n.language);
		return locale === enUS ? enGB : locale;
	}, [i18n.language]);
};

/** Same as `useDateFnsLocale`, for pickers built on `AdapterDayjs`. */
export const useDayjsLocale = (): string => {
	const { i18n } = useTranslation();
	return useMemo(() => getDayjsLocale(i18n.language), [i18n.language]);
};
